import { CircleCheckIcon, CircleXIcon, LoaderCircleIcon, BadgePlusIcon, GiftIcon } from 'lucide-react';
import { InputProps } from '@/types/form/input-props';
import { Bonus } from '@/types/api/bonus';
import { TextInput } from '@/shared/components/ui/inputs/text-input';
import { bonusValueText, thresholdText } from '@/utils/sale/bonus';
import { BonusCodeKind } from '../api/check-bonus-code';

type BonusCodeInputProps = InputProps<string> & {
  isValidating: boolean;
  /** The bonus the valid code gives. */
  bonus?: Bonus | null;
  kind?: BonusCodeKind | null;
};

export function BonusCodeInput({ value, errors, isValidating, onChange, bonus, kind, ...rest }: BonusCodeInputProps) {
  const isEmpty = !value?.trim();
  const isValid = !isEmpty && !isValidating && !errors.length;

  const adornment = isEmpty ? null : isValidating ? (
    <LoaderCircleIcon className="animate-spin text-muted-foreground" size={16} aria-hidden="true" />
  ) : isValid ? (
    <CircleCheckIcon className="text-green-600" size={16} aria-hidden="true" />
  ) : (
    <CircleXIcon className="text-red-600" size={16} aria-hidden="true" />
  );

  const hint = isValid ? (
    kind === BonusCodeKind.Referral && bonus ? (
      <p className="pt-1 flex items-start gap-1 text-xs text-green-600" aria-live="polite">
        <GiftIcon className="mt-px shrink-0" size={12} aria-hidden="true" />
        <span>
          <span className="font-semibold">Tipskod!</span> {bonusValueText(bonus)}
          {bonus.threshold && ` ${thresholdText(bonus.threshold)}`}
        </span>
      </p>
    ) : bonus ? (
      <p className="pt-1 flex items-center gap-1 text-xs text-green-600" aria-live="polite">
        <BadgePlusIcon className="shrink-0" size={12} aria-hidden="true" />
        <span>
          <span className="font-semibold">{bonusValueText(bonus)}</span>
          {bonus.threshold && ` ${thresholdText(bonus.threshold)}`}
        </span>
      </p>
    ) : (
      <p className="pt-1 text-xs text-green-600" aria-live="polite">
        Bonuskoden är giltig
      </p>
    )
  ) : (
    <p className="pt-1 text-xs text-muted-foreground">Valfritt. Har du fått en bonuskod? Ange den här.</p>
  );

  return (
    <TextInput
      {...rest}
      value={value ?? ''}
      errors={isValidating ? [] : errors}
      onChange={(v) => onChange(v.toUpperCase())}
      autoComplete="off"
      spellCheck={false}
      adornment={adornment}
      hint={hint}
    />
  );
}
